import type {ScrollToIdOptions, ScrollEasing} from "@/ReactInit/Scripts/DashboardPage/types";

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const prefersReducedMotion = () =>
  isBrowser() && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

function isElementInViewInWindow(el: HTMLElement, thresholdPx: number) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return r.bottom >= -thresholdPx && r.top <= vh + thresholdPx;
}

function isElementInViewInContainer(container: HTMLElement, el: HTMLElement, thresholdPx: number) {
  const cRect = container.getBoundingClientRect();
  const tRect = el.getBoundingClientRect();
  const top = tRect.top - cRect.top;
  const bottom = tRect.bottom - cRect.top;
  const ch = container.clientHeight;
  return bottom >= -thresholdPx && top <= ch + thresholdPx;
}

function ease(t: number, easing: ScrollEasing): number {
  switch (easing) {
    case "linear":
      return t;
    case "easeOutCubic":
      return 1 - Math.pow(1 - t, 3);
    case "easeInOutCubic":
    default:
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}

function animateScroll(opts: {
  getTop: () => number;
  setTop: (value: number) => void;
  to: number;
  durationMs: number;
  easing: ScrollEasing;
}) {
  const {getTop, setTop, to, durationMs, easing: easingName} = opts;

  const from = getTop();
  const diff = to - from;
  if (Math.abs(diff) < 1) {
    setTop(to);
    return;
  }

  const start = performance.now();

  const tick = (now: number) => {
    const elapsed = now - start;
    const t = Math.min(1, elapsed / durationMs);
    const p = ease(t, easingName);
    setTop(from + diff * p);

    if (t < 1) requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}

export function scrollToId(id: string, options: ScrollToIdOptions = {}): boolean {
  if (!isBrowser()) return false;

  const {
    behavior = "smooth",
    block = "start",
    inline = "nearest",
    offsetTop = 0,
    containerId,
    retryNextFrame = false,
    durationMs,
    easing = "easeInOutCubic",
    onlyIfNotInView = true,
    inViewThresholdPx = 8,
  } = options;

  const run = (): boolean => {
    const target = document.getElementById(id);
    if (!target) return false;

    const reduce = prefersReducedMotion();

    if (containerId) {
      const container = document.getElementById(containerId);
      if (!container) return false;

      if (onlyIfNotInView && isElementInViewInContainer(container, target, inViewThresholdPx)) {
        return true;
      }

      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const to = container.scrollTop + (tRect.top - cRect.top) - offsetTop;

      if (onlyIfNotInView && isElementInViewInWindow(target, inViewThresholdPx)) {
        return true;
      }
      
      if (!reduce && typeof durationMs === "number" && durationMs > 0) {
        animateScroll({
          getTop: () => container.scrollTop,
          setTop: (v) => (container.scrollTop = v),
          to,
          durationMs,
          easing,
        });
        return true;
      }

      container.scrollTo({top: to, behavior});
      return true;
    }

    const rect = target.getBoundingClientRect();
    const to = window.scrollY + rect.top - offsetTop;

    if (!reduce && typeof durationMs === "number" && durationMs > 0) {
      animateScroll({
        getTop: () => window.scrollY,
        setTop: (v) => window.scrollTo({top: v}),
        to,
        durationMs,
        easing,
      });
      return true;
    }

    target.scrollIntoView({behavior, block, inline});
    if (offsetTop !== 0) {
      requestAnimationFrame(() => window.scrollBy({top: -offsetTop, left: 0, behavior}));
    }
    return true;
  };

  const ok = run();

  if (!ok && retryNextFrame) {
    requestAnimationFrame(() => run());
  }

  return ok;
}
